import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ProductImageLightbox.module.css";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const getDistance = (left, right) =>
  Math.hypot(right.x - left.x, right.y - left.y);
const getMidpoint = (left, right) => ({
  x: (left.x + right.x) / 2,
  y: (left.y + right.y) / 2,
});

const ProductImageLightbox = ({
  images,
  activeIndex,
  onActiveIndexChange,
  onClose,
  productName,
}) => {
  const rootRef = useRef(null);
  const viewportRef = useRef(null);
  const imageRef = useRef(null);
  const closeButtonRef = useRef(null);
  const scaleRef = useRef(MIN_SCALE);
  const offsetRef = useRef({ x: 0, y: 0 });
  const pointersRef = useRef(new Map());
  const dragRef = useRef(null);
  const pinchRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onIndexChangeRef = useRef(onActiveIndexChange);
  const activeIndexRef = useRef(activeIndex);
  const imagesLengthRef = useRef(Array.isArray(images) ? images.length : 0);
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [sourceIndex, setSourceIndex] = useState(0);
  const [imageStatus, setImageStatus] = useState("loading");

  onCloseRef.current = onClose;
  onIndexChangeRef.current = onActiveIndexChange;
  activeIndexRef.current = activeIndex;
  imagesLengthRef.current = Array.isArray(images) ? images.length : 0;

  const safeImages = Array.isArray(images) ? images : [];
  const currentImage = useMemo(
    () => (Array.isArray(images) ? images[activeIndex] : null) || null,
    [activeIndex, images],
  );
  const sourceCandidates = useMemo(
    () =>
      [currentImage?.original, currentImage?.full, currentImage?.thumbnail]
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter((value, index, list) => value && list.indexOf(value) === index),
    [currentImage],
  );
  const currentSource = sourceCandidates[sourceIndex] || "";

  const clampOffset = (nextOffset, nextScale) => {
    const viewport = viewportRef.current;
    const image = imageRef.current;
    if (!viewport || !image) return { x: 0, y: 0 };
    const maxX = Math.max(
      0,
      (image.offsetWidth * nextScale - viewport.clientWidth) / 2,
    );
    const maxY = Math.max(
      0,
      (image.offsetHeight * nextScale - viewport.clientHeight) / 2,
    );
    return {
      x: clamp(nextOffset.x, -maxX, maxX),
      y: clamp(nextOffset.y, -maxY, maxY),
    };
  };

  const updateView = (nextScale, nextOffset) => {
    const boundedScale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    const boundedOffset =
      boundedScale === MIN_SCALE
        ? { x: 0, y: 0 }
        : clampOffset(nextOffset, boundedScale);
    scaleRef.current = boundedScale;
    offsetRef.current = boundedOffset;
    setScale(boundedScale);
    setOffset(boundedOffset);
  };

  const resetView = () => updateView(MIN_SCALE, { x: 0, y: 0 });

  const zoomAt = (nextScale, clientX, clientY) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const previousScale = scaleRef.current;
    const boundedScale = clamp(nextScale, MIN_SCALE, MAX_SCALE);
    const rect = viewport.getBoundingClientRect();
    const point = {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };
    const ratio = boundedScale / previousScale;
    updateView(boundedScale, {
      x: point.x - (point.x - offsetRef.current.x) * ratio,
      y: point.y - (point.y - offsetRef.current.y) * ratio,
    });
  };

  const showImage = (index) => {
    const length = imagesLengthRef.current;
    if (length < 2) return;
    onIndexChangeRef.current((index + length) % length);
  };

  useEffect(() => {
    setSourceIndex(0);
    setImageStatus("loading");
    pointersRef.current.clear();
    dragRef.current = null;
    pinchRef.current = null;
    scaleRef.current = MIN_SCALE;
    offsetRef.current = { x: 0, y: 0 };
    setScale(MIN_SCALE);
    setOffset({ x: 0, y: 0 });
  }, [activeIndex, currentImage]);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        showImage(activeIndexRef.current - 1);
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        showImage(activeIndexRef.current + 1);
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        rootRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleResize = () => updateView(scaleRef.current, offsetRef.current);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleResize);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
    // Refs keep callbacks and the active image current for this open session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWheel = (event) => {
    event.preventDefault();
    const factor = event.deltaY < 0 ? 1.18 : 1 / 1.18;
    zoomAt(scaleRef.current * factor, event.clientX, event.clientY);
  };

  const handlePointerDown = (event) => {
    if (event.button !== undefined && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    const points = [...pointersRef.current.values()];
    if (points.length >= 2) {
      const rect = viewportRef.current.getBoundingClientRect();
      const midpoint = getMidpoint(points[0], points[1]);
      pinchRef.current = {
        distance: Math.max(1, getDistance(points[0], points[1])),
        midpoint: {
          x: midpoint.x - (rect.left + rect.width / 2),
          y: midpoint.y - (rect.top + rect.height / 2),
        },
        scale: scaleRef.current,
        offset: offsetRef.current,
      };
      dragRef.current = null;
    } else {
      dragRef.current = {
        x: event.clientX,
        y: event.clientY,
        offset: offsetRef.current,
      };
    }
  };

  const handlePointerMove = (event) => {
    if (!pointersRef.current.has(event.pointerId)) return;
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
    });
    const points = [...pointersRef.current.values()];
    if (points.length >= 2 && pinchRef.current) {
      const rect = viewportRef.current.getBoundingClientRect();
      const midpoint = getMidpoint(points[0], points[1]);
      const currentMidpoint = {
        x: midpoint.x - (rect.left + rect.width / 2),
        y: midpoint.y - (rect.top + rect.height / 2),
      };
      const nextScale = clamp(
        pinchRef.current.scale *
          (getDistance(points[0], points[1]) / pinchRef.current.distance),
        MIN_SCALE,
        MAX_SCALE,
      );
      const contentPoint = {
        x:
          (pinchRef.current.midpoint.x - pinchRef.current.offset.x) /
          pinchRef.current.scale,
        y:
          (pinchRef.current.midpoint.y - pinchRef.current.offset.y) /
          pinchRef.current.scale,
      };
      updateView(nextScale, {
        x: currentMidpoint.x - contentPoint.x * nextScale,
        y: currentMidpoint.y - contentPoint.y * nextScale,
      });
      return;
    }
    if (points.length === 1 && dragRef.current && scaleRef.current > MIN_SCALE) {
      updateView(scaleRef.current, {
        x: dragRef.current.offset.x + event.clientX - dragRef.current.x,
        y: dragRef.current.offset.y + event.clientY - dragRef.current.y,
      });
    }
  };

  const handlePointerEnd = (event) => {
    pointersRef.current.delete(event.pointerId);
    pinchRef.current = null;
    const remaining = [...pointersRef.current.values()][0];
    dragRef.current = remaining
      ? { ...remaining, offset: offsetRef.current }
      : null;
  };

  const handleImageError = () => {
    if (sourceIndex + 1 < sourceCandidates.length) {
      setSourceIndex((current) => current + 1);
      setImageStatus("loading");
    } else {
      setImageStatus("error");
    }
  };

  return (
    <div
      ref={rootRef}
      className={styles.overlay}
      role="dialog"
      aria-modal="true"
      aria-label="Збільшений перегляд зображення товару"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCloseRef.current();
      }}
    >
      <div className={styles.dialog}>
        <div className={styles.toolbar}>
          <button
            ref={closeButtonRef}
            type="button"
            className={styles.closeButton}
            onClick={() => onCloseRef.current()}
            aria-label="Закрити перегляд"
            title="Закрити"
          >
            ×
          </button>
        </div>

        <div
          ref={viewportRef}
          className={`${styles.viewport} ${scale > MIN_SCALE ? styles.canPan : ""}`}
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          onDoubleClick={(event) => {
            if (scale > MIN_SCALE) resetView();
            else zoomAt(2, event.clientX, event.clientY);
          }}
          aria-label="Область масштабування зображення"
        >
          {imageStatus === "loading" && (
            <div className={styles.status} role="status">
              <span className={styles.spinner} aria-hidden="true" />
              Завантаження зображення…
            </div>
          )}
          {imageStatus === "error" ? (
            <div className={styles.status} role="alert">
              Не вдалося завантажити зображення.
            </div>
          ) : (
            currentSource && (
              <img
                ref={imageRef}
                src={currentSource}
                alt={`${productName || "Товар"} — фото ${activeIndex + 1}`}
                className={styles.image}
                draggable="false"
                onLoad={() => {
                  setImageStatus("ready");
                  requestAnimationFrame(resetView);
                }}
                onError={handleImageError}
                style={{
                  transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                }}
              />
            )
          )}
        </div>

        {safeImages.length > 1 && (
          <>
            <button
              type="button"
              className={`${styles.navigationButton} ${styles.previousButton}`}
              onClick={() => showImage(activeIndex - 1)}
              aria-label="Попереднє зображення"
            >
              ‹
            </button>
            <button
              type="button"
              className={`${styles.navigationButton} ${styles.nextButton}`}
              onClick={() => showImage(activeIndex + 1)}
              aria-label="Наступне зображення"
            >
              ›
            </button>
            <div className={styles.imageCounter} aria-live="polite">
              {activeIndex + 1} / {safeImages.length}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductImageLightbox;
